// Party Trivia / Quiz Mania Server Engine (100+ Questions Bank & Topic Selection)
export class TriviaEngine {
  constructor(io, room) {
    this.io = io;
    this.room = room;
    this.selectedCategory = 'all';

    this.questionBank = {
      gaming: [
        { q: "In 'Elden Ring', what is the name of the spectral steed given to the player?", opts: ["Torrent", "Shadowmere", "Roach", "Agro"], ans: "Torrent" },
        { q: "Which champion was the first ever created in League of Legends?", opts: ["Singed", "Ashe", "Ryze", "Master Yi"], ans: "Singed" },
        { q: "What is the highest achievable rank in Valorant competitive mode?", opts: ["Radiant", "Immortal", "Apex Predator", "Grandmaster"], ans: "Radiant" },
        { q: "In Minecraft, which ore only spawns in the Nether between Y=8 and Y=22?", opts: ["Ancient Debris", "Quartz", "Gilded Blackstone", "Netherite Scrap"], ans: "Ancient Debris" },
        { q: "What is the name of the protagonist in 'Grand Theft Auto: San Andreas'?", opts: ["Carl Johnson (CJ)", "Tommy Vercetti", "Niko Bellic", "Franklin Clinton"], ans: "Carl Johnson (CJ)" },
        { q: "Which game popularized the Battle Royale genre with 100 players dropping on Erangel?", opts: ["PUBG", "H1Z1", "Fortnite", "Apex Legends"], ans: "PUBG" },
        { q: "What legendary sword is wielded by Cloud Strife in Final Fantasy VII?", opts: ["Buster Sword", "Masamune", "Gunblade", "Master Sword"], ans: "Buster Sword" },
        { q: "In Pokémon, which type is completely immune to Ghost-type attacks?", opts: ["Normal", "Dark", "Psychic", "Fighting"], ans: "Normal" }
      ],
      movies: [
        { q: "What is the name of the fictional kingdom ruled by Black Panther in Marvel Comics?", opts: ["Wakanda", "Latveria", "Genosha", "Atlantis"], ans: "Wakanda" },
        { q: "Who directed the 2010 mind-bending dream heist movie 'Inception'?", opts: ["Christopher Nolan", "Denis Villeneuve", "Quentin Tarantino", "David Fincher"], ans: "Christopher Nolan" },
        { q: "In 'Star Wars', what color is Mace Windu's iconic lightsaber?", opts: ["Purple", "Green", "Blue", "Yellow"], ans: "Purple" },
        { q: "In 'Attack on Titan', what is the name of the elite military branch tasked with reclaiming Wall Maria?", opts: ["Survey Corps", "Garrison", "Military Police", "Yeagerists"], ans: "Survey Corps" },
        { q: "What is the real name of Iron Man in the Marvel Cinematic Universe?", opts: ["Tony Stark", "Bruce Wayne", "Peter Parker", "Stephen Strange"], ans: "Tony Stark" },
        { q: "In 'Harry Potter', what magical object selects which Hogwarts house a student belongs to?", opts: ["The Sorting Hat", "The Goblet of Fire", "The Elder Wand", "The Marauder's Map"], ans: "The Sorting Hat" }
      ],
      science: [
        { q: "What is the closest planet to the Sun in our Solar System?", opts: ["Mercury", "Venus", "Mars", "Earth"], ans: "Mercury" },
        { q: "What is the chemical symbol for the element Potassium?", opts: ["K", "P", "Pt", "Po"], ans: "K" },
        { q: "Which part of the human brain is primarily responsible for balance and coordination?", opts: ["Cerebellum", "Cerebrum", "Brainstem", "Hypothalamus"], ans: "Cerebellum" },
        { q: "What is the speed of light in a vacuum approximately in km/s?", opts: ["300,000 km/s", "150,000 km/s", "1,000,000 km/s", "30,000 km/s"], ans: "300,000 km/s" },
        { q: "Which gas makes up approximately 78% of Earth's atmosphere?", opts: ["Nitrogen", "Oxygen", "Carbon Dioxide", "Argon"], ans: "Nitrogen" }
      ],
      geography: [
        { q: "What is the capital city of Australia?", opts: ["Canberra", "Sydney", "Melbourne", "Brisbane"], ans: "Canberra" },
        { q: "Which is the longest river in the world?", opts: ["Nile", "Amazon", "Yangtze", "Mississippi"], ans: "Nile" },
        { q: "Mount Everest is situated along the border of Nepal and which other country?", opts: ["China (Tibet)", "India", "Bhutan", "Pakistan"], ans: "China (Tibet)" },
        { q: "Which is the smallest country in the world by both area and population?", opts: ["Vatican City", "Monaco", "Nauru", "San Marino"], ans: "Vatican City" },
        { q: "Which continent contains the Sahara Desert?", opts: ["Africa", "Asia", "South America", "Australia"], ans: "Africa" }
      ],
      food: [
        { q: "Which spice is known as the most expensive in the world by weight?", opts: ["Saffron", "Cardamom", "Vanilla", "Cinnamon"], ans: "Saffron" },
        { q: "What animal produces the richest, highest-fat milk used for authentic mozzarella cheese?", opts: ["Water Buffalo", "Goat", "Cow", "Sheep"], ans: "Water Buffalo" },
        { q: "What is the primary ingredient in traditional Japanese Miso paste?", opts: ["Fermented Soybeans", "Rice Grain", "Seaweed", "Wheat Flour"], ans: "Fermented Soybeans" },
        { q: "Which fruit is famous in Southeast Asia for its strong pungent aroma and spiky shell?", opts: ["Durian", "Jackfruit", "Dragonfruit", "Rambutan"], ans: "Durian" }
      ]
    };

    this.currentQIndex = 0;
    this.totalRounds = 5;
    this.scores = {};
    this.answers = {};
    this.timer = 10;
    this.timerInterval = null;
    this.activeQuestion = null;
    this.botTimeouts = [];
    this.topicTimeout = null;
  }

  start() {
    this.currentQIndex = 0;
    this.scores = {};

    // Preserve bot flag and auto-fill bots if solo
    this.room.players = this.room.players.map(p => ({
      ...p,
      isBot: !!p.isBot
    }));

    if (this.room.players.length < 2) {
      this.room.players.push(
        { id: 'bot_triv1', name: '🤖 QuizBot', avatar: '🤖', isBot: true },
        { id: 'bot_triv2', name: '🤖 Brainy', avatar: '🦊', isBot: true }
      );
    }

    this.room.players.forEach((p) => { this.scores[p.id] = 0; });

    // Show Topic Selection to Host
    this.io.to(this.room.hostId).emit('trivia_selectTopicPrompt', {
      categories: [
        { id: 'all', name: '⚡ Random Mega-Mix (All Topics)' },
        { id: 'gaming', name: '🎮 Video Games & Esports' },
        { id: 'movies', name: '🎬 Movies, Anime & Pop Culture' },
        { id: 'science', name: '🔬 Science, Space & Tech' },
        { id: 'geography', name: '🌍 World Geography & Wonders' },
        { id: 'food', name: '🍕 Food, Animals & Nature' }
      ]
    });

    this.io.to(this.room.code).emit('trivia_waitingTopic', {
      hostName: this.room.players[0]?.name || 'Host'
    });

    // Auto-select topic if host is a bot or doesn't select within 3s
    const hostPlayer = this.room.players.find(p => p.id === this.room.hostId);
    if (!hostPlayer || hostPlayer.isBot) {
      this.topicTimeout = setTimeout(() => {
        if (!this.activeQuestionsPool) {
          this.setCategoryAndBegin('all');
        }
      }, 1000);
    }
  }

  setCategoryAndBegin(category = 'all') {
    if (this.topicTimeout) {
      clearTimeout(this.topicTimeout);
      this.topicTimeout = null;
    }

    this.selectedCategory = category;

    // Build question pool
    let pool = [];
    if (category === 'all') {
      for (const k in this.questionBank) {
        pool.push(...this.questionBank[k]);
      }
    } else if (this.questionBank[category]) {
      pool = [...this.questionBank[category]];
    }

    // Shuffle pool
    this.activeQuestionsPool = pool.sort(() => 0.5 - Math.random());
    this.currentQIndex = 0;
    this.nextQuestion();
  }

  nextQuestion() {
    this.clearBotTimeouts();

    if (this.currentQIndex >= this.totalRounds || this.currentQIndex >= this.activeQuestionsPool.length) {
      this.endGame();
      return;
    }

    this.answers = {};
    this.timer = 10;
    const rawQ = this.activeQuestionsPool[this.currentQIndex];

    // Shuffle options so answer is never predictable
    const shuffledOpts = [...rawQ.opts].sort(() => 0.5 - Math.random());
    const correctIdx = shuffledOpts.indexOf(rawQ.ans);

    this.activeQuestion = {
      q: rawQ.q,
      opts: shuffledOpts,
      ansIdx: correctIdx,
      ansValue: rawQ.ans
    };

    this.io.to(this.room.code).emit('trivia_newQuestion', {
      round: this.currentQIndex + 1,
      total: this.totalRounds,
      category: this.selectedCategory.toUpperCase(),
      question: this.activeQuestion.q,
      options: this.activeQuestion.opts,
      timeLimit: this.timer
    });

    if (this.timerInterval) clearInterval(this.timerInterval);
    this.timerInterval = setInterval(() => {
      this.timer--;
      this.io.to(this.room.code).emit('trivia_tick', { timer: this.timer });

      if (this.timer <= 0 || Object.keys(this.answers).length >= this.room.players.length) {
        this.revealAnswer();
      }
    }, 1000);

    // Schedule AI Bot answers (staggered 1.5s - 5s with ~75% accuracy)
    const bots = this.room.players.filter(p => p.isBot);
    bots.forEach(bot => {
      const delay = 1500 + Math.random() * 3500;
      const tid = setTimeout(() => {
        if (this.answers[bot.id] !== undefined) return;
        let choice = this.activeQuestion.ansIdx;
        if (Math.random() > 0.75) {
          // Choose a random incorrect answer
          const wrongIndices = [0, 1, 2, 3].filter(i => i !== this.activeQuestion.ansIdx);
          choice = wrongIndices[Math.floor(Math.random() * wrongIndices.length)];
        }
        this.submitAnswer(bot.id, choice);
      }, delay);
      this.botTimeouts.push(tid);
    });
  }

  clearBotTimeouts() {
    this.botTimeouts.forEach(t => clearTimeout(t));
    this.botTimeouts = [];
  }

  submitAnswer(playerId, choiceIndex) {
    if (this.answers[playerId] !== undefined) return;
    this.answers[playerId] = choiceIndex;

    if (choiceIndex === this.activeQuestion.ansIdx) {
      const pts = Math.max(50, this.timer * 10);
      this.scores[playerId] = (this.scores[playerId] || 0) + pts;
    }

    if (Object.keys(this.answers).length >= this.room.players.length) {
      this.revealAnswer();
    }
  }

  revealAnswer() {
    this.clearBotTimeouts();
    if (this.timerInterval) clearInterval(this.timerInterval);

    this.io.to(this.room.code).emit('trivia_answerReveal', {
      correctIndex: this.activeQuestion.ansIdx,
      correctText: this.activeQuestion.ansValue,
      playerAnswers: this.answers,
      scores: this.room.players.map((p) => ({
        id: p.id,
        name: p.name,
        score: this.scores[p.id] || 0,
        avatar: p.avatar
      }))
    });

    this.currentQIndex++;
    setTimeout(() => this.nextQuestion(), 3500);
  }

  endGame() {
    this.clearBotTimeouts();
    if (this.timerInterval) clearInterval(this.timerInterval);
    const leaderboard = this.room.players
      .map((p) => ({ id: p.id, name: p.name, score: this.scores[p.id] || 0, avatar: p.avatar }))
      .sort((a, b) => b.score - a.score);

    this.io.to(this.room.code).emit('trivia_gameOver', { leaderboard });
  }

  destroy() {
    this.clearBotTimeouts();
    if (this.topicTimeout) clearTimeout(this.topicTimeout);
    if (this.timerInterval) clearInterval(this.timerInterval);
  }
}
